import { createClient } from '@/lib/supabase/client';

export interface HomepageContent {
  id: string;
  sectionKey: string;
  sectionTitle: string;
  content: any;
  createdAt: string;
  updatedAt: string;
}

export interface HomepageContentInput {
  sectionKey: string;
  sectionTitle: string;
  content: any;
}

export interface HeroStat {
  icon: string;
  label: string;
  value: string;
}

export interface HeroStatsContent {
  stats: HeroStat[];
}

class HomepageService {
  async getAll(): Promise<HomepageContent[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('homepage_content')
      .select('*')
      .order('section_key', { ascending: true });

    if (error) throw new Error(error.message);
    
    return (data || []).map(this.mapFromDb);
  }

  async getByKey(sectionKey: string): Promise<HomepageContent | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('homepage_content')
      .select('*')
      .eq('section_key', sectionKey)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(error.message);
    }
    
    return data ? this.mapFromDb(data) : null;
  }

  async upsert(input: HomepageContentInput): Promise<HomepageContent | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('homepage_content')
      .upsert([{
        section_key: input.sectionKey,
        section_title: input.sectionTitle,
        content: input.content,
        updated_by: user.id,
      }], {
        onConflict: 'section_key',
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async update(sectionKey: string, content: any): Promise<HomepageContent | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('homepage_content')
      .update({
        content,
        updated_by: user.id,
      })
      .eq('section_key', sectionKey)
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async delete(sectionKey: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('homepage_content')
      .delete()
      .eq('section_key', sectionKey);

    if (error) throw new Error(error.message);
  }

  async getHeroStats(): Promise<HeroStat[]> {
    try {
      const content = await this.getByKey('hero_stats');
      if (content?.content?.stats && Array.isArray(content.content.stats)) {
        return content.content.stats;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch hero stats:', error);
      return [];
    }
  }

  async updateHeroStats(stats: HeroStat[]): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('homepage_content')
      .update({
        content: { stats },
        updated_by: user.id,
      })
      .eq('section_key', 'hero_stats');

    if (error) throw new Error(error.message);
  }

  private mapFromDb(data: any): HomepageContent {
    return {
      id: data.id,
      sectionKey: data.section_key,
      sectionTitle: data.section_title,
      content: data.content,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const homepageService = new HomepageService();